/**
 * MissingData — explicit list of what hasn't been measured + an
 * action the user can take to fill the gap.
 *
 * Replaces "(default)" badges scattered through the panel. Audit
 * principle: missing data should be a separate, visible section,
 * not hidden inside theme bars or marked with cryptic labels.
 *
 * Items are derived from:
 *   - readiness_themes with defaulted=true
 *   - Critical fields we know we DON'T have (e.g. permit timeline)
 *
 * Each row shows the field name, why it's missing (in plain text),
 * and a recommended action.
 */

import React, { useEffect, useRef } from 'react';

const THEME_TO_GAP = {
  access: {
    label: 'Road Access Detail',
    reason: 'Statens Vegvesen lookup not run for this parcel.',
    action: 'Run Vegvesen verification',
  },
  water: {
    label: 'Water Catchment & Extraction',
    reason: 'NVE GridTimeSeries catchment data not yet fetched.',
    action: 'Run NVE catchment check',
  },
  permitting: {
    label: 'Permit Pathway & Timeline',
    reason: 'No quantitative model available; requires manual review.',
    action: 'Run Licence Assessment',
  },
  environment: {
    label: 'Species & NiN Habitat Detail',
    reason: 'Hazard scan completed but detailed species check not run.',
    action: 'Re-run species and NiN check',
  },
  planning: {
    label: 'Formal Zoning Status',
    reason: 'No DIBK reguleringsplan or kommuneplan in NAP for this parcel.',
    action: 'Verify with kommune planning office',
  },
  fiber: {
    label: 'Fibre Carrier List',
    reason: 'PeeringDB facility list cached but per-carrier capacity not queried.',
    action: 'Contact local carriers directly',
  },
  expansion: {
    label: 'Adjacent Parcel Ownership',
    reason: 'No adjacent-parcel ownership data fetched yet.',
    action: 'Manual Matrikkel review',
  },
};

// Project-level gaps that are never theme-driven — e.g. grid feasibility
// queue, which needs the dedicated external agent.
const FIXED_GAPS = (site) => {
  const out = [];
  if (!site.grid_queue_total_mw && !site.grid_feasibility_score) {
    out.push({
      label: 'Grid Connection Queue',
      reason: 'Statnett/SVK queue status not yet fetched for this substation.',
      action: 'Run grid feasibility & queue check',
    });
  }
  return out;
};

export default function MissingData({ site, jumpTargetField = null }) {
  if (!site) return null;
  const themes = site.readiness_themes || {};

  // Keep the theme key alongside the gap so we can stamp data-gap on
  // the row — BottomLine gap counters jump here via "gap:<key>".
  const themeGaps = Object.entries(themes)
    .filter(([_, t]) => t?.defaulted)
    .map(([key]) => THEME_TO_GAP[key] && { key, ...THEME_TO_GAP[key] })
    .filter(Boolean);

  const allGaps = [
    ...themeGaps,
    ...FIXED_GAPS(site).map(g => ({ key: null, ...g })),
  ];

  const rootRef = useRef(null);
  useEffect(() => {
    if (!jumpTargetField || !jumpTargetField.startsWith('gap:')) return;
    const themeKey = jumpTargetField.slice(4);
    const r = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (!root) return;
      const el = root.querySelector(`[data-gap="${CSS.escape(themeKey)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('jump-flash');
      setTimeout(() => el.classList.remove('jump-flash'), 1800);
    });
    return () => cancelAnimationFrame(r);
  }, [jumpTargetField]);

  if (allGaps.length === 0) return null;

  return (
    <div ref={rootRef} style={{ padding: '14px 0', borderTop: '1px solid #1f2937' }}>
      <div className="section-header">Missing Data</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
        {allGaps.map((gap, i) => (
          <div key={i} className="missing-data-item" data-gap={gap.key || undefined}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span className="status-dot status-unknown" />
              <span style={{ fontWeight: 600, fontSize: 13 }}>{gap.label}</span>
            </div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2, paddingLeft: 14 }}>
              {gap.reason}
            </div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4, paddingLeft: 14, fontStyle: 'italic' }}>
              Action: {gap.action}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
